import { MoreVert } from "@mui/icons-material";
import { Avatar, Box, IconButton, Stack, Typography } from "@mui/material";
import { AcxMenuItem, AcxMenuV2 } from "components/UI/Menu/AcxMenu";
import AcxChip from "components/UI/AcxChip";
import { observer } from "mobx-react";
import moment from "moment";
import { forwardRef, useRef, useState } from "react";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import { ChangeRecordDialog } from "../../Components/Approvals/ChangeRecordDialog";
import {
    ApprovalStatusChip,
    ApprovalStatusIcon,
} from "../../Components/Approvals/ApprovalStatusChip";
import {
    initials,
    llmClassifierVersionCreator,
    llmClassifierVersionLabel,
} from "../../utils/llmClassifierVersionDisplay";

export const LLMClassifierVersionHistoryItem = observer(
    forwardRef<
        HTMLDivElement,
        {
            version: LLMClassifierImplementation;
            isCurrent: boolean;
            isCompact: boolean;
            isPublished: boolean;
            onSelect: () => void;
        }
    >(function LLMClassifierVersionHistoryItem(
        { version, isCurrent, isCompact, isPublished, onSelect },
        ref,
    ) {
        const createdBy = llmClassifierVersionCreator(version);
        const versionLabel = llmClassifierVersionLabel(version.createdOn);
        const menuAnchorRef = useRef<HTMLButtonElement | null>(null);
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const [isChangeRecordOpen, setIsChangeRecordOpen] = useState(false);

        const badges = [
            isCurrent && (
                <AcxChip
                    key="viewing"
                    size="small"
                    color="primary800"
                    label="Viewing"
                    sx={{
                        textTransform: "uppercase",
                        color: "white.main",
                        backgroundColor: "primary.700",
                    }}
                />
            ),
            isPublished && (
                <AcxChip
                    key="published"
                    size="small"
                    color="secondary800"
                    label="Published"
                    sx={{ textTransform: "uppercase" }}
                />
            ),
        ].filter(Boolean);

        return (
            <Box
                ref={ref}
                px={isCompact ? 1.5 : 2}
                py={isCompact ? 1.25 : 2}
                sx={(theme) => ({
                    borderBottom: `1px solid ${theme.palette.gray[200]}`,
                    "&:last-child": { borderBottom: 0 },
                    // The loaded version is the only cue
                    // that switching versions did anything.
                    borderLeft: `3px solid ${
                        isCurrent ? theme.palette.primary.main : "transparent"
                    }`,
                    backgroundColor: isCurrent
                        ? theme.palette.primary[50]
                        : "transparent",
                })}
            >
                <Stack
                    direction="row"
                    // Chip text is smaller, so centering
                    // leaves the two off by a pixel or two.
                    alignItems={isCompact ? "center" : "baseline"}
                    flexWrap="wrap"
                    rowGap={0.5}
                    gap={1}
                >
                    <VersionLabelButton
                        isCompact={isCompact}
                        isCurrent={isCurrent}
                        onSelect={onSelect}
                    >
                        {versionLabel}
                    </VersionLabelButton>
                    {!isCompact && badges}
                    {/* Spelled out where there is room, an icon where there is not. */}
                    <Box
                        ml="auto"
                        display="flex"
                        alignItems="center"
                        gap={0.25}
                    >
                        {isCompact ? (
                            <ApprovalStatusIcon
                                status={version.approvalStatus}
                            />
                        ) : (
                            <>
                                <ApprovalStatusChip
                                    status={version.approvalStatus}
                                />
                                {/* Compact is sized to the label alone, so the menu waits for the wider list. */}
                                <IconButton
                                    ref={menuAnchorRef}
                                    size="small"
                                    aria-label="Version actions"
                                    onClick={() =>
                                        setIsMenuOpen((open) => !open)
                                    }
                                    // Slimmed so long status chips stay on the label's line.
                                    sx={{ p: 0.25, my: -0.5, mr: -0.75 }}
                                >
                                    <MoreVert fontSize="small" />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Stack>

                {/* Too narrow to sit beside the label, so compact wraps them under it. */}
                {isCompact && badges.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {badges}
                    </Stack>
                )}

                {/* Compact keeps the author but drops the note, the avatar and
                    a timestamp the version label already carries. */}
                {!isCompact && version.versionDescription && (
                    <Typography color="gray.700" variant="body2" mt={1}>
                        {version.versionDescription}
                    </Typography>
                )}

                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    mt={isCompact ? 0.5 : 1.5}
                >
                    {!isCompact && (
                        <Avatar
                            sx={(theme) => ({
                                width: 24,
                                height: 24,
                                fontSize: "10px",
                                fontWeight: 600,
                                backgroundColor: theme.palette.purple[100],
                                color: theme.palette.purple[700],
                            })}
                        >
                            {initials(createdBy)}
                        </Avatar>
                    )}
                    <Typography color="gray.500" variant="caption" noWrap>
                        {isCompact
                            ? createdBy
                            : `${createdBy} · ${moment
                                  .utc(version.createdOn)
                                  .local()
                                  .format("MMM D · HH:mm")}`}
                    </Typography>
                </Stack>

                <AcxMenuV2
                    anchorEl={menuAnchorRef.current}
                    open={isMenuOpen && !isCompact}
                    onClose={() => setIsMenuOpen(false)}
                >
                    <AcxMenuItem
                        onClick={() => {
                            setIsChangeRecordOpen(true);
                            setIsMenuOpen(false);
                        }}
                    >
                        View change record
                    </AcxMenuItem>
                </AcxMenuV2>

                <ChangeRecordDialog
                    isOpen={isChangeRecordOpen}
                    onClose={() => setIsChangeRecordOpen(false)}
                    implementationId={version.id}
                    versionLabel={versionLabel}
                />
            </Box>
        );
    }),
);

function VersionLabelButton(props: {
    isCompact: boolean;
    isCurrent: boolean;
    onSelect: () => void;
    children: React.ReactNode;
}) {
    return (
        <Typography
            component="button"
            type="button"
            aria-current={props.isCurrent ? "true" : undefined}
            disabled={props.isCurrent}
            onClick={props.onSelect}
            sx={(theme) => ({
                border: 0,
                padding: 0,
                background: "none",
                textAlign: "left",
                font: "inherit",
                fontSize: props.isCompact ? "13px" : "15px",
                fontWeight: 600,
                color: props.isCurrent
                    ? theme.palette.gray[900]
                    : theme.palette.gray[700],
                cursor: props.isCurrent ? "default" : "pointer",
                textDecoration: props.isCurrent ? "none" : "underline",
                textUnderlineOffset: "2px",
                textDecorationColor: theme.palette.gray[400],
                "&:hover": {
                    textDecorationColor: "currentColor",
                },
                "&:focus-visible": {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: "2px",
                    borderRadius: "2px",
                },
            })}
        >
            {props.children}
        </Typography>
    );
}
